import React, { ComponentPropsWithoutRef, FC, MouseEvent } from 'react';

import { keyframes, styled, useTheme } from '@mui/material';
import { css } from '@mui/material/styles';

import { Svg } from '~/assets/icons/styles';
import useAnimationPlayState from '~/hooks/useAnimationPlayState';

interface ThumbUpIconProps extends ComponentPropsWithoutRef<typeof Svg> {
  animated?: boolean;
  filled?: boolean;
}

const DURATION_IN_S = 1;
const DURATION_IN_MS = DURATION_IN_S * 1000;

const ThumbUpIcon: FC<ThumbUpIconProps> = ({
  color,
  filled,
  animated,
  onClick,
  onMouseEnter,
  ...props
}) => {
  const theme = useTheme();
  const iconColor = color || theme.palette.common.white;

  const { trigger: triggerStarsAnimation, playing: starsAnimationPlaying } =
    useAnimationPlayState(DURATION_IN_MS);
  const { trigger: triggerHandAnimation, playing: handAnimationPlaying } =
    useAnimationPlayState(DURATION_IN_MS);

  const handleClick = (e: MouseEvent<SVGSVGElement>) => {
    if (!animated) {
      return;
    }

    triggerStarsAnimation();
    onClick?.(e);
  };

  const handleMouseEnter = (e: MouseEvent<SVGSVGElement>) => {
    if (!animated) {
      return;
    }

    triggerHandAnimation();
    onMouseEnter?.(e);
  };

  return (
    <Wrapper
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      starsAnimationPlaying={starsAnimationPlaying}
      handAnimationPlaying={handAnimationPlaying}
      {...props}
      onMouseEnter={handleMouseEnter}
      onClick={handleClick}
      svgColor={iconColor}
    >
      <g className="thumb">
        <path
          d="M10.0508 20.1923C10.4029 20.5444 10.826 20.7205 11.3201 20.7205H18.5067C18.8511 20.7205 19.1805 20.6157 19.4949 20.4061C19.8093 20.1965 20.0339 19.942 20.1687 19.6425L22.8636 13.3093C22.908 13.2045 22.9415 13.0922 22.9643 12.9724C22.987 12.8526 22.9984 12.7403 22.9984 12.6355V10.8389C22.9984 10.3598 22.8187 9.94054 22.4594 9.58121C22.1001 9.22187 21.6808 9.04221 21.2017 9.04221H16.3058L17.3165 4.90989C17.3919 4.62542 17.3659 4.34843 17.2383 4.07893C17.1107 3.80943 16.9047 3.59982 16.6203 3.4501C16.3364 3.30038 16.0408 3.24798 15.7336 3.29289C15.4264 3.33781 15.1605 3.46507 14.9359 3.67468L10.0624 8.52567C9.89834 8.69096 9.76749 8.88201 9.66987 9.0988C9.57225 9.3156 9.52344 9.54378 9.52344 9.78333V18.9238C9.52284 19.4173 9.69861 19.8401 10.0508 20.1923Z"
          stroke="currentCOlor"
          strokeWidth="2"
          fill={filled ? 'currentColor' : undefined}
        />
        <rect
          x="2.5"
          y="9.1582"
          width="3.97932"
          height="11.5625"
          rx="1"
          stroke="currentColor"
          strokeWidth="2"
          fill={filled ? 'currentColor' : undefined}
        />
      </g>
      <StarsGroup visible={starsAnimationPlaying}>
        <g className="bigStar">
          <path
            fill="currentColor"
            fillOpacity="1"
            d=" M11.140000343322754,1.6469999551773071 C11.833999633789062,-0.550000011920929 14.942000389099121,-0.550000011920929 15.63599967956543,1.6469999551773071 C15.63599967956543,1.6469999551773071 17.545000076293945,7.692999839782715 17.545000076293945,7.692999839782715 C17.775999069213867,8.425999641418457 18.350000381469727,9 19.08300018310547,9.230999946594238 C19.08300018310547,9.230999946594238 25.128000259399414,11.140000343322754 25.128000259399414,11.140000343322754 C27.325000762939453,11.833999633789062 27.325000762939453,14.942000389099121 25.128000259399414,15.63599967956543 C25.128000259399414,15.63599967956543 19.08300018310547,17.545000076293945 19.08300018310547,17.545000076293945 C18.350000381469727,17.775999069213867 17.775999069213867,18.350000381469727 17.545000076293945,19.08300018310547 C17.545000076293945,19.08300018310547 15.63599967956543,25.128000259399414 15.63599967956543,25.128000259399414 C14.942000389099121,27.325000762939453 11.833999633789062,27.325000762939453 11.140000343322754,25.128000259399414 C11.140000343322754,25.128000259399414 9.230999946594238,19.08300018310547 9.230999946594238,19.08300018310547 C9,18.350000381469727 8.425999641418457,17.775999069213867 7.692999839782715,17.545000076293945 C7.692999839782715,17.545000076293945 1.6469999551773071,15.63599967956543 1.6469999551773071,15.63599967956543 C-0.550000011920929,14.942000389099121 -0.550000011920929,11.833999633789062 1.6469999551773071,11.140000343322754 C1.6469999551773071,11.140000343322754 7.692999839782715,9.230999946594238 7.692999839782715,9.230999946594238 C8.425999641418457,9 9,8.425999641418457 9.230999946594238,7.692999839782715 C9.230999946594238,7.692999839782715 11.140000343322754,1.6469999551773071 11.140000343322754,1.6469999551773071z"
          ></path>
        </g>
        <g className="circle">
          <path
            fill="currentColor"
            fillOpacity="1"
            d=" M0,-6 C3.3113999366760254,-6 6,-3.3113999366760254 6,0 C6,3.3113999366760254 3.3113999366760254,6 0,6 C-3.3113999366760254,6 -6,3.3113999366760254 -6,0 C-6,-3.3113999366760254 -3.3113999366760254,-6 0,-6z"
          ></path>
        </g>
        <g className="smallStar">
          <path
            fill="currentColor"
            fillOpacity="1"
            d=" M8.576000213623047,1.2680000066757202 C9.109999656677246,-0.4230000078678131 11.503000259399414,-0.4230000078678131 12.036999702453613,1.2680000066757202 C12.036999702453613,1.2680000066757202 13.505999565124512,5.921999931335449 13.505999565124512,5.921999931335449 C13.684000015258789,6.486000061035156 14.12600040435791,6.927999973297119 14.6899995803833,7.105999946594238 C14.6899995803833,7.105999946594238 19.3439998626709,8.576000213623047 19.3439998626709,8.576000213623047 C21.03499984741211,9.109999656677246 21.03499984741211,11.503000259399414 19.3439998626709,12.036999702453613 C19.3439998626709,12.036999702453613 14.6899995803833,13.505999565124512 14.6899995803833,13.505999565124512 C14.12600040435791,13.684000015258789 13.684000015258789,14.12600040435791 13.505999565124512,14.6899995803833 C13.505999565124512,14.6899995803833 12.036999702453613,19.3439998626709 12.036999702453613,19.3439998626709 C11.503000259399414,21.03499984741211 9.109999656677246,21.03499984741211 8.576000213623047,19.3439998626709 C8.576000213623047,19.3439998626709 7.105999946594238,14.6899995803833 7.105999946594238,14.6899995803833 C6.927999973297119,14.12600040435791 6.486000061035156,13.684000015258789 5.921999931335449,13.505999565124512 C5.921999931335449,13.505999565124512 1.2680000066757202,12.036999702453613 1.2680000066757202,12.036999702453613 C-0.4230000078678131,11.503000259399414 -0.4230000078678131,9.109999656677246 1.2680000066757202,8.576000213623047 C1.2680000066757202,8.576000213623047 5.921999931335449,7.105999946594238 5.921999931335449,7.105999946594238 C6.486000061035156,6.927999973297119 6.927999973297119,6.486000061035156 7.105999946594238,5.921999931335449 C7.105999946594238,5.921999931335449 8.576000213623047,1.2680000066757202 8.576000213623047,1.2680000066757202z"
          ></path>
        </g>
      </StarsGroup>
    </Wrapper>
  );
};

export default ThumbUpIcon;

const Wrapper = styled(Svg, {
  shouldForwardProp: (prop) => prop !== 'handAnimationPlaying' && prop !== 'starsAnimationPlaying',
})<{ handAnimationPlaying?: boolean; starsAnimationPlaying?: boolean }>`
  overflow: visible;

  & .thumb {
    animation-name: ${({ handAnimationPlaying }) => handAnimationPlaying && thumbKeyframes};
    animation-duration: ${DURATION_IN_S}s;
  }

  & .bigStar,
  & .circle,
  & .smallStar {
    animation-duration: ${DURATION_IN_S}s;
    transform: matrix(0, 0, 0, 0, 0, 0);
  }

  ${({ starsAnimationPlaying }) =>
    starsAnimationPlaying &&
    css`
      & .bigStar {
        animation-name: ${bigStarKeyframes};
      }

      & .circle {
        animation-name: ${circleKeyframes};
      }

      & .smallStar {
        animation-name: ${smallStarKeyframes};
      }
    `}
`;
const StarsGroup = styled('g', {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible?: boolean }>`
  display: ${({ visible }) => !visible && 'none'};
`;
const thumbKeyframes = keyframes`
    0% {
        transform: matrix(1, 0, 0, 1, 0, 0);
    }
    3.33% {
        transform: matrix(0.9460224509239197, 0.04757580906152725, -0.04757580906152725, 0.9460224509239197, 1.343135357, 0.7591583939);
    }
    6.66% {
        transform: matrix(0.8954156637191772, 0.08590327948331833, -0.08590327948331833, 0.8954156637191772, 2.4792943, 1.499072248);
    }
    9.99% {
        transform: matrix(0.8851245045661926, 0.09303033351898193, -0.09303033351898193, 0.8851245045661926, 2.697248936, 1.652735125)
    }
    13.32% {
        transform: matrix(0.8913347125053406, 0.08193352818489075, -0.08193352818489075, 0.8913347125053406, 2.436917782, 1.524558434);
    }
    16.65% {
        transform: matrix(0.9125640988349915, 0.022111346945166588, -0.022111346945166588, 0.9125640988349915, 1.105130196, 0.9980221403);
    }
    19.98% {
        transform: matrix(0.9374565482139587, -0.03430943563580513, 0.03430943563580513, 0.9374565482139587, -0.2223912477, 0.4950725563);
    }
    23.31% {
        transform: matrix(0.95736163854599, -0.0670795738697052, 0.0670795738697052, 0.95736163854599, -1.042208195, 0.1728744936);
    }
    26.64% {
        transform: matrix(0.9711341857910156, -0.08826050907373428, 0.08826050907373428, 0.9711341857910156, -1.579813659, -0.03371068729);
    }
    29.97% {
        transform: matrix(0.9803303480148315, -0.10251793265342712, 0.10251793265342712, 0.9803303480148315, -1.941369981, -0.1675335596);
    }
    33.3% {
        transform: matrix(0.9863176345825195, -0.11201051622629166, 0.11201051622629166, 0.9863176345825195, -2.181205885, -0.2534075481);
    }
    36.63% {
        transform: matrix(0.9899923801422119, -0.1179618239402771, 0.1179618239402771, 0.9899923801422119, -2.331041299, -0.3057397553);
    }
    39.96% {
        transform: matrix(0.991930365562439, -0.12116113305091858, 0.12116113305091858, 0.991930365562439, -2.411336973, -0.3332850186);
    }
    43.29% {
        transform: matrix(0.9925461411476135, -0.12186934053897858, 0.12186934053897858, 0.9925461411476135, -2.430355921, -0.3410718678);
    }
    46.62% {
        transform: matrix(0.9941509366035461, -0.10799935460090637, 0.10799935460090637, 0.9941509366035461, -2.150026839, -0.3134038614);
    }
    49.95% {
        transform: matrix(0.9969800114631653, -0.07765869051218033, 0.07765869051218033, 0.9969800114631653, -1.54016459, -0.2428373297);
    }
    53.28% {
        transform: matrix(0.9992311000823975, -0.039207495748996735, 0.039207495748996735, 0.9992311000823975, -0.7738553286, -0.1337412822);
    }
    56.61% {
        transform: matrix(1, 0, 0, 1, 0, 0);
    }
    59.94% {
        transform: matrix(0.9998961091041565, 0.014415920712053776, -0.014415920712053776, 0.9998961091041565, 0.2826271057, 0.05487366868);
    }
    63.27% {
        transform: matrix(0.9997512698173523, 0.02230345830321312, -0.02230345830321312, 0.9997512698173523, 0.4368302822, 0.08619354992);
    }
    66.6% {
        transform: matrix(0.9997082948684692, 0.024153007194399834, -0.024153007194399834, 0.9997082948684692, 0.4729452133, 0.09367049971);
    }
    69.93% {
        transform: matrix(0.9997714757919312, 0.02137865498661995, -0.02137865498661995, 0.9997714757919312, 0.4187664986, 0.08247332546);
    }
    73.26% {
        transform: matrix(0.9998757839202881, 0.015759671106934547, -0.015759671106934547, 0.9998757839202881, 0.3089189529, 0.06014482165);
    }
    76.59% {
        transform: matrix(0.9999589920043945, 0.009058134630322456, -0.009058134630322456, 0.9999589920043945, 0.1777069569, 0.03412114299);
    }
    79.92% {
        transform: matrix(0.9999963641166687, 0.0027052571531385183, -0.0027052571531385183, 0.9999963641166687, 0.05311536789, 0.01006421631);
    }
    83.25% {
        transform: matrix(0.9999973773956299, -0.002286379225552082, 0.002286379225552082, 0.9999973773956299, -0.04491949081, -0.008421677809);
    }
    86.58% {
        transform: matrix(0.999984860420227, -0.00549775967374444, 0.00549775967374444, 0.999984860420227, -0.1080548763, -0.02012020204);
    }
    89.91% {
        transform: matrix(0.9999765753746033, -0.006841637194156647, 0.006841637194156647, 0.9999765753746033, -0.1344907284, -0.02497052162);
    }
    93.24% {
        transform: matrix(0.9999778866767883, -0.006649655289947987, 0.006649655289947987, 0.9999778866767883, -0.1307137012, -0.02427915247);
    }
    96.57% {
        transform: matrix(0.9999854564666748, -0.005393041297793388, 0.005393041297793388, 0.9999854564666748, -0.1059949398, -0.01974123728);
    }
    100% {
        transform: matrix(1, 0, 0, 1, 0, 0);
    }
`;
const bigStarKeyframes = keyframes`
    0% {
        transform: matrix(0, 0, 0, 0, 19.55599976, 1.403001996) scale(0.25);
    }
    16.65% {
        transform: matrix(0, 0, 0, 0, 19.55599976, 1.403001996) scale(0.25);
    }
    19.98% {
        transform: matrix(0.0038856363389641047, -0.04658407345414162, 0.04658407345414162, 0.0038856363389641047, 19.7574501, 1.28706669) scale(0.25);
    }
    23.31% {
        transform: matrix(0.08492349088191986, -0.204322949051857, 0.204322949051857, 0.08492349088191986, 19.76068008, 0.6385724495) scale(0.25);
    }
    26.64% {
        transform: matrix(0.3618253469467163, -0.3151591420173645, 0.3151591420173645, 0.3618253469467163, 19.69317093, -1.024374327) scale(0.25);
    }
    29.97% {
        transform: matrix(0.6484254002571106, -0.2326032817363739, 0.2326032817363739, 0.6484254002571106, 19.88335159, -2.950012035) scale(0.25);
    }
    33.3% {
        transform: matrix(0.8224920034408569, -0.08195587992668152, 0.08195587992668152, 0.8224920034408569, 20.25744217, -4.413359608) scale(0.25);
    }
    36.63% {
        transform: matrix(0.9119976162910461, 0.050444912165403366, -0.050444912165403366, 0.9119976162910461, 20.62621109, -5.370566635) scale(0.25);
    }
    39.96% {
        transform: matrix(0.9547770023345947, 0.14197899401187897, -0.14197899401187897, 0.9547770023345947, 20.90302444, -5.940017557) scale(0.25);
    }
    43.29% {
        transform: matrix(0.9732058644294739, 0.19254335761070251, -0.19254335761070251, 0.9732058644294739, 21.06470169, -6.229115069) scale(0.25);
    }
    46.62% {
        transform: matrix(0.9781476259231567, 0.2079116851091385, -0.2079116851091385, 0.9781476259231567, 21.11531082, -6.313501111) scale(0.25);
    }
    49.95% {
        transform: matrix(0.8103232979774475, 0.23132547736167908, -0.23132547736167908, 0.8103232979774475, 21.67223098, -6.10540866) scale(0.25);
    }
    53.28% {
        transform: matrix(0.45498064160346985, 0.2073466181755066, -0.2073466181755066, 0.45498064160346985, 22.76719192, -5.361973188) scale(0.25);
    }
    56.61% {
        transform: matrix(0.13182595372200012, 0.08582936227321625, -0.08582936227321625, 0.13182595372200012, 23.58160965, -4.046870262) scale(0.25);
    }
    59.94% {
        transform: matrix(0, 0, 0, 0, 23.84521804, -3.381238154) scale(0.25);
    }
    100% {
        transform: matrix(0, 0, 0, 0, 23.84521804, -3.381238154) scale(0.25);
    }
`;
const circleKeyframes = keyframes`
    0% {
        transform: matrix(0, 0, 0, 0, 14.1875, 1.031894934) scale(0.25);
    }
    16.65% {
        transform: matrix(0, 0, 0, 0, 14.1875, 1.031894934) scale(0.25);
    }
    19.98% {
        transform: matrix(0.046745844185352325, 0, 0, 0.046745844185352325, 14.03471088, 0.6555424846) scale(0.25);
    }
    23.31% {
        transform: matrix(0.22126877307891846, 0, 0, 0.22126877307891846, 13.48914433, -0.7551110931) scale(0.25);
    }
    26.64% {
        transform: matrix(0.47983628511428833, 0, 0, 0.47983628511428833, 12.67997169, -2.844899591) scale(0.25);
    }
    29.97% {
        transform: matrix(0.6888830065727234, 0, 0, 0.6888830065727234, 11.99871159, -4.528400285) scale(0.25);
    }
    33.3% {
        transform: matrix(0.8265650868415833, 0, 0, 0.8265650868415833, 11.52014542, -5.630112052) scale(0.25);
    }
    36.63% {
        transform: matrix(0.913391649723053, 0, 0, 0.913391649723053, 11.1923151, -6.318193976) scale(0.25);
    }
    39.96% {
        transform: matrix(0.9652757048606873, 0, 0, 0.9652757048606873, 10.97594261, -6.723526674) scale(0.25);
    }
    43.29% {
        transform: matrix(0.9920698404312134, 0, 0, 0.9920698404312134, 10.85195732, -6.928928798) scale(0.25);
    }
    46.62% {
        transform: matrix(1, 0, 0, 1, 10.8125, -6.988742964) scale(0.25);
    }
    49.95% {
        transform: matrix(0.8426952958106995, 0, 0, 0.8426952958106995, 10.59590244, -7.259995852) scale(0.25);
    }
    53.28% {
        transform: matrix(0.5, 0, 0, 0.5, 9.130784035, -7.094476818) scale(0.25);
    }
    56.61% {
        transform: matrix(0.15730467438697815, 0, 0, 0.15730467438697815, 8.8125, -6.894934334) scale(0.25);
    }
    59.94% {
        transform: matrix(0, 0, 0, 0, 8.8125, -6.894934334) scale(0.25);
    }
    100% {
        transform: matrix(0, 0, 0, 0, 8.8125, -6.894934334) scale(0.25);
    }
`;
const smallStarKeyframes = keyframes`
    0% {
        transform: matrix(0, 0, 0, 0, 7.549750328, 4.018386354) scale(0.25);
    }
    16.65% {
        transform: matrix(0, 0, 0, 0, 7.549750328, 4.018386354) scale(0.25);
    }
    19.98% {
        transform: matrix(0.0038856363389641047, 0.04658407345414162, -0.04658407345414162, 0.0038856363389641047, 7.392436028, 3.652543288) scale(0.25);
    }
    23.31% {
        transform: matrix(0.08492349088191986, 0.204322949051857, -0.204322949051857, 0.08492349088191986, 6.614941597, 2.176670554) scale(0.25);
    }
    26.64% {
        transform: matrix(0.3618253469467163, 0.3151591420173645, -0.3151591420173645, 0.3618253469467163, 4.741700172, -0.07526548003) scale(0.25);
    }
    29.97% {
        transform: matrix(0.6484254002571106, 0.2326032817363739, -0.2326032817363739, 0.6484254002571106, 2.597291946, -1.670667065) scale(0.25);
    }
    33.3% {
        transform: matrix(0.8224920034408569, 0.08195587992668152, -0.08195587992668152, 0.8224920034408569, 0.9475710392, -2.4912063) scale(0.25);
    }
    36.63% {
        transform: matrix(0.9119976162910461, -0.050444912165403366, 0.050444912165403366, 0.9119976162910461, -0.1604771614, -2.882695869) scale(0.25);
    }
    39.96% {
        transform: matrix(0.9547770023345947, -0.14197899401187897, 0.14197899401187897, 0.9547770023345947, -0.8453614712, -3.059612653) scale(0.25);
    }
    43.29% {
        transform: matrix(0.9732058644294739, -0.19254335761070251, 0.19254335761070251, 0.9732058644294739, -1.208919764, -3.129435928) scale(0.25);
    }
    46.62% {
        transform: matrix(0.9781476259231567, -0.2079116851091385, 0.2079116851091385, 0.9781476259231567, -1.318631649, -3.146350853) scale(0.25);
    }
    49.95% {
        transform: matrix(0.8036677241325378, -0.25348278880119324, 0.25348278880119324, 0.8036677241325378, -1.344641924, -2.894145686) scale(0.25);
    }
    53.28% {
        transform: matrix(0.43517786264419556, -0.24621178209781647, 0.24621178209781647, 0.43517786264419556, -1.179978013, -1.997939462) scale(0.25);
    }
    56.61% {
        transform: matrix(0.11782453954219818, -0.10422158241271973, 0.10422158241271973, 0.11782453954219818, -0.3871290684, -1.026343941) scale(0.25);
    }
    59.94% {
        transform: matrix(0, 0, 0, 0, -0.01275002956, -0.7189494808) scale(0.25);
    }
    100% {
        transform: matrix(0, 0, 0, 0, -0.01275002956, -0.7189494808) scale(0.25);
    }
`;
